import {
  forwardRef,
  useEffect,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "../lib/cn";

/**
 * 14px box, 4px radius, 1.5px border — filled brand when checked.
 *
 * The visual box is a `<span>`, but a REAL `<input type="checkbox">` sits
 * behind it, visually hidden and still focusable. Rebuilding a checkbox out of
 * divs loses form participation, the space key, indeterminate state and every
 * assistive-tech affordance at once — and none of that loss shows up in a
 * screenshot, which is why it keeps getting rebuilt.
 *
 * The tick is driven by COLOUR, not opacity: `peer-checked:` compiles to a
 * sibling selector, so it can only reach the box, never the tick nested inside
 * it. Setting the box's text colour and drawing the tick with `currentColor`
 * lets ordinary inheritance carry the state the one level down.
 *
 * `indeterminate` is a prop here because it is a DOM PROPERTY with no HTML
 * attribute — React will not set it from JSX, and it has to be written to the
 * node after every render. Leaving that to call sites means the one place it
 * matters, a table's select-all with some rows chosen, quietly renders as an
 * ordinary unchecked box and tells the user the wrong thing.
 */
export type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "size"
> & {
  /** The third state: some, but not all, of what this stands for. */
  indeterminate?: boolean;
  children?: ReactNode;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox(
    { className, children, disabled, indeterminate = false, ...props },
    ref,
  ) {
    const inner = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (inner.current) inner.current.indeterminate = indeterminate;
    });

    return (
      <label
        className={cn(
          "inline-flex cursor-pointer items-center gap-2 text-body text-text",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <input
          ref={(node) => {
            inner.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
          }}
          type="checkbox"
          disabled={disabled}
          className="peer sr-only"
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            "grid size-3.5 shrink-0 place-items-center rounded-[4px]",
            "border-[1.5px] border-border-active text-transparent transition-colors",
            "peer-checked:border-brand peer-checked:bg-brand peer-checked:text-on-brand",
            "peer-indeterminate:border-brand peer-indeterminate:bg-brand peer-indeterminate:text-on-brand",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-info/60",
          )}
        >
          <svg
            viewBox="0 0 16 16"
            className="size-2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* A dash for "some", a tick for "all" — a different mark, not a
                dimmer tick, because dimness does not read as a third state. */}
            <path d={indeterminate ? "M3.5 8h9" : "M3 8.5 6.5 12 13 4.5"} />
          </svg>
        </span>
        {children}
      </label>
    );
  },
);
